import React, { useCallback, useDeferredValue, useMemo, useState } from 'react';
import { Goal, SubGoal, Todo } from '../services/goalService';
import GoalCard from '../components/GoalCard';
import CompletedGoalsGroup, { splitCompleted } from '../components/CompletedGoalsGroup/CompletedGoalsGroup';
import Logo from '../components/Logo';
import Icon from '../components/Icon';
import FullscreenInspiration from '../components/FullscreenInspiration';
import EmptyState from '../components/EmptyState/EmptyState';
import { INACTIVITY_TIMEOUT } from '../constants';
import useInactivityTimer from '../hooks/useInactivityTimer';
import { useInspirationScreenPreference } from '../hooks/useInspirationScreenPreference';
import { useGoalsPage } from '../hooks/useGoalsPage';
import { useTranslation } from '../i18n';
import MotivationBanner from '../components/MotivationBanner/MotivationBanner';
import { GoalCardsSkeleton } from '../components/Skeleton/Skeleton';
import './GoalsPage.css';

interface GoalsPageProps {
  goals: Goal[];
  loading?: boolean;
  error: string | null;
  onLoadGoals: () => void;
  onShowForm: () => void;
  onShowAIModal: () => void;
  onShowDrafts: () => void;
  onShowCreationModal?: () => void;
  onUpdateGoal: (id: string, data: Partial<Goal>) => void;
  onToggleFavorite: (id: string) => void;
  onAddSubGoal: (goalId: string, title: string, description: string) => void;
  onUpdateSubGoal: (goalId: string, subGoalId: string, data: Partial<SubGoal>) => void;
  onDeleteSubGoal: (goalId: string, subGoalId: string) => void;
  onUpdateTodo?: (subGoalId: string, todoId: string, data: Partial<Todo>) => void;
  onNavigateToTasks?: () => void;
}

const GoalsPage: React.FC<GoalsPageProps> = ({
  goals,
  loading = false,
  error,
  onLoadGoals,
  onShowForm,
  onShowAIModal,
  onShowDrafts,
  onShowCreationModal,
  onUpdateGoal,
  onToggleFavorite,
  onAddSubGoal,
  onUpdateSubGoal,
  onDeleteSubGoal,
  onUpdateTodo,
  onNavigateToTasks,
}) => {
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const { t } = useTranslation();
  const deferredGoals = useDeferredValue(goals);
  const {
    filteredGoals,
    searchTerm,
    setSearchTerm,
    sortBy,
    setSortBy,
    filterBy,
    setFilterBy,
  } = useGoalsPage(deferredGoals);

  const { enabled: inspirationEnabled } = useInspirationScreenPreference();

  useInactivityTimer(() => {
    setIsFullscreenOpen(true);
  }, { timeout: INACTIVITY_TIMEOUT, enabled: inspirationEnabled });

  const handleOpenGoal = useCallback((goalId: string) => {
    window.location.hash = `#/goals/${goalId}`;
  }, []);

  const renderCard = useCallback((goal: Goal) => (
    <div key={goal.id} className="goal-card-wrapper">
      <GoalCard
        goal={goal}
        onUpdateGoal={onUpdateGoal}
        onToggleFavorite={onToggleFavorite}
        onAddSubGoal={onAddSubGoal}
        onUpdateSubGoal={onUpdateSubGoal}
        onDeleteSubGoal={onDeleteSubGoal}
        onUpdateTodo={onUpdateTodo}
        onNavigateToTasks={onNavigateToTasks}
        isListView={true}
        onClick={() => handleOpenGoal(goal.id)}
      />
    </div>
  ), [
    onUpdateGoal,
    onToggleFavorite,
    onAddSubGoal,
    onUpdateSubGoal,
    onDeleteSubGoal,
    onUpdateTodo,
    onNavigateToTasks,
    handleOpenGoal
  ]);

  // Completed goals fold under the open ones (T-127), unless the filter asks for them alone
  const groups = useMemo(
    () => (filterBy === 'completed'
      ? { active: filteredGoals, completed: [] as Goal[] }
      : splitCompleted(filteredGoals)),
    [filteredGoals, filterBy]
  );

  const hasGoals = deferredGoals.length > 0;
  const hasFilteredResults = filteredGoals.length > 0;
  const filtersActive = Boolean(searchTerm.trim()) || filterBy !== 'all';

  const filterOptions: Array<'all' | 'favorites' | 'completed' | 'active'> = [
    'all',
    'favorites',
    'completed',
    'active',
  ];

  return (
    <>
    {error && (
        <div className="error-message">
          <p>{error}</p>
      <button onClick={onLoadGoals} className="retry-btn"><Icon name="retry" /> {t('common.retry')}</button>
        </div>
      )}

      <div className="goals-section">
        {loading && !hasGoals ? (
          <GoalCardsSkeleton count={6} />
        ) : !hasGoals ? (
          <EmptyState
            classPrefix="goals-empty"
            visual={
              <div className="goals-empty-visual">
                <div className="visual-backdrop"></div>
                <div className="visual-icon-wrapper">
                  <Logo size={80} className="visual-logo" />
                </div>
                <div className="visual-sparkle sparkle-1">✦</div>
                <div className="visual-sparkle sparkle-2">✦</div>
                <div className="visual-sparkle sparkle-3">✦</div>
              </div>
            }
            title={t('goals.noGoals')}
            description={t('goals.noGoalsSubtitle')}
            actions={
              <div className="goals-empty-buttons">
                <button onClick={onShowAIModal} className="goals-empty-cta cta-ai">
                  <Icon name="sparkles" className="cta-icon" />
                  {t('goals.addGoalAI')}
                </button>
                <button onClick={onShowForm} className="goals-empty-cta cta-manual">
                  <Icon name="edit" className="cta-icon" />
                  {t('goals.addGoalManual')}
                </button>
                <button onClick={onShowDrafts} className="goals-empty-cta cta-drafts">
                  <Icon name="sparkles" className="cta-icon" />
                  {t('aiDrafts.title')}
                </button>
              </div>
            }
          >
            <div className="goals-empty-features">
              <div className="feature-item">
                <Icon name="goal" className="feature-icon" />
                <span>{t('home.empty.feature1')}</span>
              </div>
              <div className="feature-item">
                <Icon name="chart" className="feature-icon" />
                <span>{t('home.empty.feature2')}</span>
              </div>
              <div className="feature-item">
                <Icon name="circleCheck" className="feature-icon" />
                <span>{t('home.empty.feature3')}</span>
              </div>
            </div>
          </EmptyState>
        ) : (
          <>
            <MotivationBanner goals={deferredGoals} />
            <div className="goals-section-header">
              <h3 className="goals-section-title">
                <Icon name="goal" className="goals-section-title-icon" />
                {t('navigation.goals')}
              </h3>
              <button onClick={onShowDrafts} className="goals-drafts-btn">
                <Icon name="sparkles" className="goals-drafts-sparkle" />
                {t('aiDrafts.title')}
                <Icon name="chevronRight" className="goals-drafts-arrow" />
              </button>
            </div>

            <div className="goals-toolbar">
              <div className="goals-search">
                <Icon name="search" className="goals-search-icon" />
                <input
                  type="search"
                  className="goals-search-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('goals.search.placeholder')}
                  aria-label={t('goals.search.placeholder')}
                />
              </div>
              <div className="goals-filter-chips" role="group" aria-label={t('goals.filter.label')}>
                {filterOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`goals-filter-chip${filterBy === option ? ' is-active' : ''}`}
                    onClick={() => setFilterBy(option)}
                  >
                    {t(`goals.filter.${option}`)}
                  </button>
                ))}
              </div>
              <label className="goals-sort">
                <span className="goals-sort-label">{t('goals.sort.label')}</span>
                <select
                  className="goals-sort-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                >
                  <option value="updated">{t('goals.sort.updated')}</option>
                  <option value="name">{t('goals.sort.name')}</option>
                  <option value="progress">{t('goals.sort.progress')}</option>
                  <option value="date">{t('goals.sort.date')}</option>
                  <option value="priority">{t('goals.sort.priority')}</option>
                </select>
              </label>
            </div>

            {!hasFilteredResults ? (
              <EmptyState
                classPrefix="goals-empty"
                title={t('goals.noResults')}
                description={
                  filtersActive
                    ? t('goals.noResultsSubtitle')
                    : t('goals.noGoalsSubtitle')
                }
              />
            ) : (
              <>
                {groups.active.length > 0 && (
                  <div className="goals-simple-layout">
                    {groups.active.map(renderCard)}
                  </div>
                )}
                <CompletedGoalsGroup
                  place="goals"
                  goals={groups.completed}
                  renderCard={renderCard}
                  listClassName="goals-simple-layout"
                  alwaysOpen={groups.active.length === 0}
                />
              </>
            )}

            <button
              className="fab-add-goal"
              onClick={onShowCreationModal || onShowForm}
              aria-label={t('goals.addGoal')}
              title={t('goals.addGoal')}
            >
              <Icon name="plus" />
            </button>
          </>
        )}
      </div>

      <FullscreenInspiration
        isOpen={isFullscreenOpen}
        onClose={() => setIsFullscreenOpen(false)}
      />
    </>
  );
};

export default React.memo(GoalsPage);
